import type React from 'react';
import { createContext, type ReactNode, useContext } from 'react';

/**
 * What an internal surface hosting the widget (today: the backoffice Playground) adds to it.
 *
 * A widget on a client site has no host: nothing extra is rendered under an answer, and the
 * chat request carries no proof of who is asking, so the API answers it as a visitor. Every
 * Playground-only affordance hangs off this provider rather than off a flag in the request,
 * which is what keeps it out of visitor-facing traffic.
 */
export interface PreviewHost {
    /** Extra content under a finished answer, keyed by its message id. */
    renderAnswerAddon?: (messageId: string) => ReactNode;
    /**
     * The signed-in backoffice user's access token, sent as a bearer on the chat request so
     * the API can tell a staff/client preview from a site visitor. Read per request: the
     * Supabase session refreshes and a token captured at mount would go stale.
     */
    getAuthToken?: () => Promise<string | null>;
    /**
     * The client page this widget stands in for, e.g. a path-rooted workspace's root URL.
     * The host's own URL says nothing about the workspace, so widget config and the chat
     * request use this instead; the API only honours it from a trusted origin, and only on
     * the requested site. Read once per widget mount: remount to change it.
     */
    pageUrl?: string;
}

const PreviewHostContext = createContext<PreviewHost | null>(null);

export const PreviewHostProvider: React.FC<{ host: PreviewHost; children: ReactNode }> = ({
    host,
    children,
}) => <PreviewHostContext.Provider value={host}>{children}</PreviewHostContext.Provider>;

/** The hosting surface, or `null` in the widget — where there is none. */
export function usePreviewHost(): PreviewHost | null {
    return useContext(PreviewHostContext);
}

/** What to render under this answer — `null` in the widget, where no host exists. */
export function useAnswerAddon(messageId: string | undefined): ReactNode {
    const host = usePreviewHost();
    if (!host?.renderAnswerAddon || !messageId) return null;
    return host.renderAnswerAddon(messageId);
}
